import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import {
    LocalNotifications,
    LocalNotificationSchema,
} from '@capacitor/local-notifications';
import { IncomingCall } from '../plugins/incoming-call.plugin';

type NotificationScope = 'alerts' | 'calls';
const CALLS_CHANNEL_ID = 'connect-calls-v2';

@Injectable({
    providedIn: 'root',
})
export class NativeNotificationService {
    private listenersRegistered = false;
    private permissionRequest?: Promise<void>;
    private appStateListenersRegistered = false;

    constructor(private router: Router) {}

    isNativeAndroid(): boolean {
        return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
    }

    async init(): Promise<void> {
        if (!this.isNativeAndroid()) {
            return;
        }

        await this.createChannels();

        if (this.listenersRegistered) {
            return;
        }

        this.listenersRegistered = true;

        await LocalNotifications.addListener(
            'localNotificationActionPerformed',
            (event: any) => {
                const conversationId = this.parseConversationId(
                    event?.notification?.extra?.conversationId
                );
                const route = event?.notification?.extra?.route;

                if (conversationId) {
                    this.router
                        .navigate(['conversations'], {
                            queryParams: { id: conversationId },
                        })
                        .catch((error) => {
                            console.error(
                                'Failed to route notification to conversation:',
                                error
                            );
                        });
                    return;
                }

                if (route) {
                    this.router.navigate([route]).catch((error) => {
                        console.error('Failed to route notification action:', error);
                    });
                }
            }
        );

        if (!this.appStateListenersRegistered) {
            this.appStateListenersRegistered = true;

            await App.addListener('appStateChange', ({ isActive }) => {
                if (isActive) {
                    void this.requestStartupPermissions(true);
                }
            });

            await App.addListener('resume', () => {
                void this.requestStartupPermissions(true);
            });
        }
    }

    async requestStartupPermissions(force = false): Promise<void> {
        if (!this.isNativeAndroid()) {
            return;
        }

        if (!force && this.permissionRequest) {
            await this.permissionRequest;
            return;
        }

        this.permissionRequest = this.performStartupPermissionRequest();

        try {
            await this.permissionRequest;
        } finally {
            this.permissionRequest = undefined;
        }
    }

    async notifyAlert(
        message: string,
        kind: 'sms' | 'email' | 'alert' = 'alert',
        conversationId?: string | number | null
    ): Promise<void> {
        if (!this.isNativeAndroid() || !this.shouldShowSystemNotification()) {
            return;
        }

        const titleMap = {
            sms: 'New SMS',
            email: 'New Email',
            alert: 'New Alert',
        };

        await this.scheduleNotification({
            id: this.generateId(),
            title: titleMap[kind],
            body: message,
            channelId: 'connect-alerts',
            extra: {
                scope: 'alerts',
                route: conversationId ? '/conversations' : '/alerts',
                kind,
                conversationId: conversationId || null,
            },
        });
    }

    async notifyIncomingCall(callData: any): Promise<void> {
        if (!this.isNativeAndroid() || !this.shouldShowSystemNotification()) {
            return;
        }

        try {
            await IncomingCall.showIncomingCall({
                callerName:
                    callData?.caller_name ||
                    callData?.customer_name ||
                    callData?.from_number ||
                    'Incoming caller',
                callerNumber: callData?.from_number || callData?.caller_number || '',
                conversationId: callData?.conversation_id ? `${callData.conversation_id}` : null,
                callSid: callData?.call_sid || null,
                dealerId: callData?.dealer_id ? `${callData.dealer_id}` : null,
                toNumber: callData?.to_number || null,
            });
            return;
        } catch (error) {
            console.error('Failed to show native incoming call UI:', error);
        }

        const caller =
            callData?.caller_name ||
            callData?.customer_name ||
            callData?.from_number ||
            'Incoming caller';

        const body = callData?.message || `Incoming call from ${caller}`;

        await this.scheduleNotification({
            id: this.generateId(),
            title: 'Incoming Call',
            body,
            channelId: CALLS_CHANNEL_ID,
            extra: {
                scope: 'calls',
                route: '/conversations',
                conversationId: callData?.conversation_id || null,
                callSid: callData?.call_sid || null,
            },
        });
    }

    async clearAlertNotifications(): Promise<void> {
        await this.clearNotificationsByScope('alerts');
    }

    async clearCallNotifications(): Promise<void> {
        try {
            await IncomingCall.dismissIncomingCall();
        } catch (error) {
            console.error('Failed to dismiss native incoming call UI:', error);
        }
        await this.clearNotificationsByScope('calls');
    }

    shouldShowSystemNotification(): boolean {
        return this.isNativeAndroid();
    }

    async ensureCallPermissions(): Promise<boolean> {
        if (!this.isNativeAndroid()) {
            return true;
        }

        await this.ensureNotificationPermission();
        return this.ensureMicrophonePermission();
    }

    private async createChannels(): Promise<void> {
        try {
            await LocalNotifications.createChannel({
                id: 'connect-alerts',
                name: 'Connect Alerts',
                description: 'SMS, email, and alert notifications',
                importance: 5,
                visibility: 1,
            });

            await LocalNotifications.createChannel({
                id: CALLS_CHANNEL_ID,
                name: 'Connect Calls',
                description: 'Incoming call notifications',
                importance: 5,
                visibility: 1,
                sound: 'default',
            });
        } catch (error) {
            console.error('Failed to create Android notification channels:', error);
        }
    }

    private async performStartupPermissionRequest(): Promise<void> {
        await this.ensureNotificationPermission();
        await this.ensureMicrophonePermission();
    }

    private async ensureNotificationPermission(): Promise<void> {
        try {
            const permissionStatus = await LocalNotifications.checkPermissions();
            if (permissionStatus.display !== 'granted') {
                await LocalNotifications.requestPermissions();
            }
        } catch (error) {
            console.error('Failed to request notification permission:', error);
        }
    }

    private async ensureMicrophonePermission(): Promise<boolean> {
        if (
            !navigator?.mediaDevices ||
            typeof navigator.mediaDevices.getUserMedia !== 'function'
        ) {
            return false;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            stream.getTracks().forEach((track) => track.stop());
            return true;
        } catch (error) {
            console.error('Microphone permission not granted:', error);
            return false;
        }
    }

    private async scheduleNotification(
        notification: LocalNotificationSchema
    ): Promise<void> {
        try {
            await LocalNotifications.schedule({
                notifications: [
                    {
                        ...notification,
                        schedule: { at: new Date(Date.now() + 250) },
                    },
                ],
            });
        } catch (error) {
            console.error('Failed to schedule local notification:', error);
        }
    }

    private async clearNotificationsByScope(scope: NotificationScope): Promise<void> {
        if (!this.isNativeAndroid()) {
            return;
        }

        try {
            const delivered = await LocalNotifications.getDeliveredNotifications();
            const deliveredMatches = delivered.notifications.filter(
                (notification: any) => notification?.extra?.scope === scope
            );

            if (deliveredMatches.length > 0) {
                await LocalNotifications.removeDeliveredNotifications({
                    notifications: deliveredMatches,
                });
            }

            const pending = await LocalNotifications.getPending();
            const pendingMatches = pending.notifications
                .filter((notification: any) => notification?.extra?.scope === scope)
                .map((notification: any) => ({ id: notification.id }));

            if (pendingMatches.length > 0) {
                await LocalNotifications.cancel({
                    notifications: pendingMatches,
                });
            }
        } catch (error) {
            console.error(`Failed to clear ${scope} notifications:`, error);
        }
    }

    private generateId(): number {
        return Math.floor(Date.now() % 2147483647);
    }

    private parseConversationId(value: string | number | null | undefined): number | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    }
}
